/**
 * SpacewarApi.ts — Cliente da API do SpaceWar no monolito EnsinoSocial.
 *
 * O jogo vive em games.ensinosocial.com.br, mas o backend de auth/persistencia
 * vive em ensinosocial.com.br. Cookie de sessao tem domain=.ensinosocial.com.br
 * + SameSite=Lax, entao a sessao e compartilhada sem CORS-credentials magic.
 *
 * Endpoints planejados (em ensinosocial.com.br/?a=spacewar.api):
 *   GET ?op=quem-sou           — retorna sessao ativa (compartilhado com outros jogos)
 *   GET ?op=criar-token        — gera token Redis pro WebSocket (Fase 2)
 *   GET ?op=ranking-global     — placar geral (Fase futura)
 *
 * Status atual:
 *   Fase 1: `quem-sou` usa o endpoint do logicbugs (mesma resposta, ja existe).
 *   Fase 2: criar `spacewar.api` no monolito e trocar `API_OP_BASE` abaixo.
 */

const HOST = location.hostname;

const API_BASE: string = (() => {
  if (HOST === 'localhost' || HOST === '127.0.0.1') {
    return 'http://localhost:8888';
  }
  // games.ensinosocial.com.br -> ensinosocial.com.br
  return 'https://ensinosocial.com.br';
})();

const API_OP_BASE = 'spacewar.api';

export interface QuemSouResposta {
  logado: boolean;
  admin?: boolean;
  usuario?: { id: number; nome: string; avatar: string };
}

export interface TokenResposta {
  ok: boolean;
  token?: string;
  expira_em?: number;
  motivo?: string;
}

export class SpacewarApi {
  private quemSouCache: QuemSouResposta | null = null;
  private quemSouPromise: Promise<QuemSouResposta> | null = null;

  /** URL pra login no monolito, com redirect de volta pro jogo. */
  get loginUrl(): string {
    const voltaPra = location.href;
    return `${API_BASE}/?a=auth.login&redirect=${encodeURIComponent(voltaPra)}`;
  }

  /** Consulta o backend pra saber se ha sessao ativa. Cacheado. */
  async quemSou(forcarRefresh = false): Promise<QuemSouResposta> {
    if (!forcarRefresh && this.quemSouCache) return this.quemSouCache;
    if (this.quemSouPromise) return this.quemSouPromise;

    this.quemSouPromise = this.fetchJson<QuemSouResposta>({ op: 'quem-sou' })
      .then((r) => {
        this.quemSouCache = r;
        this.quemSouPromise = null;
        return r;
      })
      .catch(() => {
        this.quemSouPromise = null;
        return { logado: false } as QuemSouResposta;
      });

    return this.quemSouPromise;
  }

  /**
   * Gera um token Redis pra abrir o WebSocket do jogo.
   * Token e one-time-use (TTL 5min). Chamar imediatamente antes de conectar.
   */
  async criarToken(): Promise<TokenResposta> {
    try {
      return await this.fetchJson<TokenResposta>({ op: 'criar-token' });
    } catch {
      return { ok: false, motivo: 'network' };
    }
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  private async fetchJson<T>(params: Record<string, string | number>): Promise<T> {
    const url = new URL(`${API_BASE}/`);
    url.searchParams.set('a', API_OP_BASE);
    for (const [k, v] of Object.entries(params)) url.searchParams.set(k, String(v));

    const r = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return (await r.json()) as T;
  }
}
